import { useState, useEffect } from 'react';
import { X, AlertCircle, CheckCircle, Trash2, Clock, Cross as InProgress, CheckSquare, FileX, CornerLeftDown } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../lib/AuthContext';
import { useTranslation } from '../lib/LanguageContext';

interface SharedOutboxProps {
  isOpen: boolean;
  onClose: () => void;
}

interface SentTodo {
  id: string;
  recipient_email: string;
  sender_email: string;
  text: string;
  notes: string;
  message: string;
  status: 'pending' | 'accepted' | 'in_progress' | 'done' | 'rejected' | 'deleted';
  created_at: string;
  accepted_at: string | null;
  deadline: string | null;
}

export function SharedOutbox({ isOpen, onClose }: SharedOutboxProps) {
  const [sentTodos, setSentTodos] = useState<SentTodo[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [reclaimingId, setReclaimingId] = useState<string | null>(null);
  const [projects, setProjects] = useState<{id: string, name: string}[]>([]);
  const [selectedProject, setSelectedProject] = useState<string | null>(null);
  const { user } = useAuth();
  const { t } = useTranslation();

  useEffect(() => {
    if (isOpen && user) {
      fetchSentTodos();
      fetchProjects();
    }
  }, [isOpen, user]);

  const fetchProjects = async () => {
    if (!user) return;

    try {
      const { data, error } = await supabase
        .from('projects')
        .select('id, name')
        .eq('user_id', user.id as any)
        .order('created_at', { ascending: true });

      if (error) throw error;

      // Cast data to the expected type
      const projectsData = (data || []) as {id: string, name: string}[];
      setProjects(projectsData);
      // Set the first project as default selected project
      if (projectsData.length > 0 && !selectedProject) {
        setSelectedProject(projectsData[0].id);
      }
    } catch (err) {
      console.error('Error fetching projects:', err);
    }
  };

  const fetchSentTodos = async () => {
    if (!user?.email) return;

    try {
      setLoading(true);

      // Query using sender_email instead of sender_id
      const { data, error } = await supabase
        .from('shared_todos')
        .select('*')
        .eq('sender_email', user.email.toLowerCase() as any)
        .order('created_at', { ascending: false });

      if (error) throw error;

      // Cast data to the expected type
      setSentTodos((data || []) as any);
    } catch (err) {
      setError(t('failedToLoadSentNotes'));
    } finally {
      setLoading(false);
    }
  };

  const deleteSharedTodo = async (id: string) => {
    if (!user?.email) return;

    try {
      setDeleting(true);

      const { error } = await supabase
        .from('shared_todos')
        .delete()
        .eq('id', id as any)
        .eq('sender_email', user.email.toLowerCase() as any);

      if (error) throw error;

      // Update the local state by removing the deleted todo
      setSentTodos(prevTodos => prevTodos.filter(todo => todo.id !== id));

    } catch (err) {
      setError(t('failedToDeleteShared'));
    } finally {
      setDeleting(false);
      setDeleteConfirmId(null);
    }
  };

  const handleReclaimTodo = async (todo: SentTodo) => {
    if (!user || !selectedProject) {
      setError(t('selectProjectToReclaim'));
      return;
    }

    try {
      setReclaimingId(todo.id);

      // Build notes (Markdown): combine original notes with message if exists
      let combinedNotes = todo.notes || '';
      if (todo.message) {
        const messageSection = `**Original message:** ${todo.message}`;
        combinedNotes = combinedNotes
          ? `${combinedNotes}\n\n---\n\n${messageSection}`
          : messageSection;
      }

      // Create a new todo in the selected project
      const { error: createError } = await supabase
        .from('todos')
        .insert([{
          user_id: user.id,
          project_id: selectedProject,
          text: todo.text,
          status: 'backlog',
          notes: combinedNotes,
          priority: false,
          color: null,
          deadline: todo.deadline
        }] as any)
        .select()
        .single();

      if (createError) throw createError;

      // Delete the shared todo record
      if (!user.email) throw new Error('User email is required');

      const { error: deleteError } = await supabase
        .from('shared_todos')
        .delete()
        .eq('id', todo.id as any)
        .eq('sender_email', user.email.toLowerCase() as any);

      if (deleteError) throw deleteError;

      // Update the local state by removing the reclaimed todo
      setSentTodos(prevTodos => prevTodos.filter(t => t.id !== todo.id));

      // Dispatch event to refresh todos
      window.dispatchEvent(new Event('refreshTodos'));

    } catch (err) {
      setError(t('failedToReclaim') + ': ' + (err instanceof Error ? err.message : String(err)));
    } finally {
      setReclaimingId(null);
    }
  };

  const getStatusDisplay = (todo: SentTodo) => {
    switch (todo.status) {
      case 'accepted':
        return (
          <span className="flex items-center text-blue-600">
            <CheckCircle className="h-4 w-4 mr-1" />
            {t('accept')}
          </span>
        );
      case 'in_progress':
        return (
          <span className="flex items-center text-indigo-600">
            <InProgress className="h-4 w-4 mr-1" />
            {t('statusInProgress')}
          </span>
        );
      case 'done':
        return (
          <span className="flex items-center text-green-600">
            <CheckSquare className="h-4 w-4 mr-1" />
            {t('statusDone')}
          </span>
        );
      case 'rejected':
        return (
          <span className="flex items-center text-red-600">
            <AlertCircle className="h-4 w-4 mr-1" />
            {t('statusRejected')}
          </span>
        );
      case 'deleted':
        return (
          <span className="flex items-center text-gray-600">
            <FileX className="h-4 w-4 mr-1" />
            {t('statusDeleted')}
          </span>
        );
      default: // 'pending'
        return (
          <span className="flex items-center text-amber-600">
            <Clock className="h-4 w-4 mr-1" />
            {t('statusWaiting')}
          </span>
        );
    }
  };

  if (!isOpen) return null;
  
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md max-h-[80vh] flex flex-col">
        <div className="flex justify-between items-center border-b px-6 py-4">
          <h3 className="text-lg font-semibold text-gray-900">{t('sentStickyNotes')}</h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-500"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="flex-1 overflow-auto px-6 py-4 max-h-[50vh]">
          {loading ? (
            <div className="text-center py-6">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mx-auto mb-2"></div>
              <p className="text-gray-500">{t('loadingSentNotes')}</p>
            </div>
          ) : error ? (
            <div className="bg-red-50 p-4 rounded-md text-red-700 mb-4">
              {error}
              <button
                onClick={() => setError(null)}
                className="ml-2 text-red-700 hover:text-red-900"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          ) : sentTodos.length === 0 ? (
            <div className="text-center py-6 text-gray-500">
              <p>{t('noSentNotes')}</p>
            </div>
          ) : (
            <div className="space-y-4">
              {sentTodos.map(todo => (
                <div key={todo.id} className="border rounded-md p-4 bg-gray-50">
                  <div className="font-medium text-gray-800 mb-2">{todo.text}</div>
                  
                  {(todo.message || todo.status === 'rejected') && (
                    <div className="mb-3 p-2 bg-indigo-50 rounded-md text-sm text-gray-700">
                      <p className="text-xs font-medium text-gray-500 mb-1">{t('messageLabel')}:</p>
                      {todo.status === 'rejected' ? t('messageReturnedToSender') : todo.message}
                    </div>
                  )}

                  <div className="flex justify-between items-center text-xs text-gray-500 mb-2">
                    <div>{t('to')}: {todo.recipient_email}</div>
                    <div>{new Date(todo.created_at).toLocaleDateString()}</div>
                  </div>
                  
                  <div className="mt-2 flex justify-between items-center">
                    <div>{getStatusDisplay(todo)}</div>
                    <div className="flex items-center space-x-2">
                      {todo.status === 'accepted' && todo.accepted_at && (
                        <div className="text-xs text-gray-500">
                          {t('acceptedOn')} {new Date(todo.accepted_at).toLocaleDateString()}
                        </div>
                      )}

                      {/* Reclaim button for pending (waiting) sticky notes */}
                      {todo.status === 'pending' && (
                        <button
                          onClick={() => setReclaimingId(todo.id)}
                          className="text-indigo-500 hover:text-indigo-700 p-1"
                          title={t('reclaimNote')}
                        >
                          <CornerLeftDown className="h-4 w-4" />
                        </button>
                      )}

                      {deleteConfirmId === todo.id ? (
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={() => setDeleteConfirmId(null)}
                            className="text-xs bg-gray-200 text-gray-700 px-2 py-1 rounded hover:bg-gray-300"
                            disabled={deleting}
                          >
                            {t('cancel')}
                          </button>
                          <button
                            onClick={() => deleteSharedTodo(todo.id)}
                            className="text-xs bg-red-500 text-white px-2 py-1 rounded hover:bg-red-600"
                            disabled={deleting}
                          >
                            {deleting ? t('deleting') : t('confirm')}
                          </button>
                        </div>
                      ) : (
                        <button
                          onClick={() => setDeleteConfirmId(todo.id)}
                          className="text-red-500 hover:text-red-700 p-1"
                          title={t('removeFromBoard')}
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  </div>
                  
                  {/* Project selector for reclaiming a sticky note */}
                  {reclaimingId === todo.id && (
                    <div className="mt-3 border-t pt-3">
                      <div className="text-sm font-medium mb-2">{t('reclaimToProject')}:</div>
                      <div className="flex space-x-2">
                        <select
                          value={selectedProject || ''}
                          onChange={(e) => setSelectedProject(e.target.value)}
                          className="flex-1 text-sm border rounded px-2 py-1"
                        >
                          {projects.map(project => (
                            <option key={project.id} value={project.id}>
                              {project.name}
                            </option>
                          ))}
                        </select>
                        <button
                          onClick={() => handleReclaimTodo(todo)}
                          disabled={!selectedProject}
                          className="px-3 py-1 bg-indigo-600 text-white rounded text-sm hover:bg-indigo-700 disabled:opacity-50"
                        >
                          {t('reclaim')}
                        </button>
                        <button
                          onClick={() => setReclaimingId(null)}
                          className="px-2 py-1 bg-gray-200 text-gray-700 rounded text-sm hover:bg-gray-300"
                        >
                          {t('cancel')}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
        
        <div className="border-t px-6 py-4">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500 w-full"
          >
            {t('close')}
          </button>
        </div>
      </div>
    </div>
  );
}